import {Component, Property} from '@wonderlandengine/api';

/**
 * OpacityController - Simple, reusable component for smooth opacity transitions
 * 
 * Usage:
 * - Attach to any object with a mesh component
 * - Call fadeIn() or fadeOut() to trigger transitions
 * - Can be used for sky spheres, objects, UI elements, etc.
 */
export class OpacityController extends Component {
    static TypeName = 'opacity-controller';
    static Properties = {
        initialOpacity: Property.float(1.0),
        fadeInDuration: Property.float(2.0),
        fadeOutDuration: Property.float(2.0),
        autoStart: Property.bool(false),
        autoStartDirection: Property.enum(['fade-in', 'fade-out'], 'fade-in'),
        debugMode: Property.bool(false)
    };
    
    init() {
        this.currentOpacity = this.initialOpacity;
        this.targetOpacity = this.initialOpacity;
        this.isTransitioning = false;
        this.transitionProgress = 0;
        this.transitionDuration = 0;
        
        // Store reference to mesh and material
        this.meshComponent = null;
        this.material = null;
        this.originalMaterial = null;
        this.materialClone = null;
    }
    
    start() {
        // Get mesh component
        this.meshComponent = this.object.getComponent('mesh');
        if (!this.meshComponent) {
            console.error('[OpacityController] No mesh component found on object!');
            return;
        }
        
        // Store original material and create a clone for opacity control
        this.originalMaterial = this.meshComponent.material;
        if (!this.originalMaterial) {
            console.error('[OpacityController] No material found on mesh!');
            return;
        }
        
        // Clone material for independent opacity control
        this.materialClone = this.originalMaterial.clone();
        this.meshComponent.material = this.materialClone;
        
        // Set initial opacity
        this.setOpacity(this.initialOpacity);
        
        if (this.debugMode) {
            console.log('[OpacityController] Initialized with opacity:', this.initialOpacity);
            console.log('[OpacityController] Fade in duration:', this.fadeInDuration);
            console.log('[OpacityController] Fade out duration:', this.fadeOutDuration);
            
            // Setup debug controls for testing
            this.setupDebugControls();
        }
        
        // Auto-start if enabled
        if (this.autoStart) {
            if (this.autoStartDirection === 0) { // fade-in
                this.fadeIn();
            } else { // fade-out
                this.fadeOut();
            }
        }
    }
    
    update(dt) {
        if (!this.isTransitioning) return;
        
        // Update transition progress
        this.transitionProgress += dt / this.transitionDuration;
        
        if (this.transitionProgress >= 1.0) {
            // Complete transition
            this.currentOpacity = this.targetOpacity;
            this.setOpacity(this.currentOpacity);
            this.isTransitioning = false;
            this.transitionProgress = 1.0;
            
            if (this.debugMode) {
                console.log('[OpacityController] Transition complete. Opacity:', this.currentOpacity);
            }
            
            // Trigger completion callback if set
            if (this.onComplete) {
                this.onComplete();
                this.onComplete = null;
            }
            return;
        }
        
        // Calculate smooth transition using easing
        const t = this.smoothstep(0, 1, this.transitionProgress);
        
        // Interpolate opacity
        const startOpacity = this.targetOpacity > this.currentOpacity ? 
            this.currentOpacity : this.currentOpacity;
        const endOpacity = this.targetOpacity;
        const currentOpacity = startOpacity + (endOpacity - startOpacity) * t;
        
        // Apply opacity
        this.setOpacity(currentOpacity);
        
        if (this.debugMode && Math.floor(t * 10) !== Math.floor((t - dt / this.transitionDuration) * 10)) {
            console.log(`[OpacityController] Progress: ${Math.round(t * 100)}%, Opacity: ${currentOpacity.toFixed(2)}`);
        }
    }
    
    setOpacity(opacity) {
        if (!this.materialClone) return;
        
        // Clamp opacity between 0 and 1
        opacity = Math.max(0, Math.min(1, opacity));
        
        // Apply opacity based on material type
        if (this.materialClone.getDiffuseColor) {
            // Phong materials - use diffuse color alpha
            const color = this.materialClone.getDiffuseColor();
            this.materialClone.setDiffuseColor([color[0], color[1], color[2], opacity]);
            
            // For emissive materials (like sky), modulate RGB not alpha!
            // This makes it fade to black instead of transparent
            if (this.materialClone.setEmissiveColor) {
                // Modulate emissive intensity, not alpha
                this.materialClone.setEmissiveColor([opacity, opacity, opacity, 1.0]);
            }
        } else if (this.materialClone.getFlatColor) {
            // Flat materials
            const color = this.materialClone.getFlatColor();
            this.materialClone.setFlatColor([color[0], color[1], color[2], opacity]);
        }
        
        this.currentOpacity = opacity;
    }
    
    // Easing function - using inverse square for perceptually linear fade
    // (Human perception of brightness is non-linear, this compensates)
    smoothstep(edge0, edge1, x) {
        const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
        
        // Use the same curve for both directions - inverse square
        // This starts slow and accelerates, making it appear more linear
        return 1 - Math.pow(1 - t, 2);
        
        // Alternative: just use linear (uncomment if you prefer)
        // return t;
        
        // Alternative: even more aggressive curve for very linear appearance
        // return 1 - Math.pow(1 - t, 3);
    }
    
    // Public API
    
    /**
     * Fade in to full opacity
     * @param {Function} onComplete - Optional callback when complete
     */
    fadeIn(onComplete = null) {
        if (this.debugMode) {
            console.log('[OpacityController] Starting fade in');
        }
        
        this.startOpacity = this.currentOpacity;
        this.targetOpacity = 1.0;
        this.transitionDuration = this.fadeInDuration;
        this.transitionProgress = 0;
        this.isTransitioning = true;
        this.onComplete = onComplete;
    }
    
    /**
     * Fade out to zero opacity
     * @param {Function} onComplete - Optional callback when complete
     */
    fadeOut(onComplete = null) {
        if (this.debugMode) {
            console.log('[OpacityController] Starting fade out');
        }
        
        this.startOpacity = this.currentOpacity;
        this.targetOpacity = 0.0;
        this.transitionDuration = this.fadeOutDuration;
        this.transitionProgress = 0;
        this.isTransitioning = true;
        this.onComplete = onComplete;
    }
    
    /**
     * Fade to specific opacity
     * @param {number} targetOpacity - Target opacity (0-1)
     * @param {number} duration - Transition duration in seconds
     * @param {Function} onComplete - Optional callback when complete
     */
    fadeTo(targetOpacity, duration, onComplete = null) {
        if (this.debugMode) {
            console.log(`[OpacityController] Fading to ${targetOpacity} over ${duration}s`);
        }
        
        this.startOpacity = this.currentOpacity;
        this.targetOpacity = Math.max(0, Math.min(1, targetOpacity));
        this.transitionDuration = duration;
        this.transitionProgress = 0;
        this.isTransitioning = true;
        this.onComplete = onComplete;
    }
    
    /**
     * Stop current transition
     */
    stop() {
        this.isTransitioning = false;
        this.onComplete = null;
        
        if (this.debugMode) {
            console.log('[OpacityController] Transition stopped at opacity:', this.currentOpacity);
        }
    }
    
    /**
     * Set opacity immediately without transition
     * @param {number} opacity - Opacity value (0-1)
     */
    setOpacityImmediate(opacity) {
        this.currentOpacity = opacity;
        this.targetOpacity = opacity;
        this.isTransitioning = false;
        this.setOpacity(opacity);
        
        if (this.debugMode) {
            console.log('[OpacityController] Opacity set immediately to:', opacity);
        }
    }
    
    /**
     * Get current opacity
     * @returns {number} Current opacity (0-1)
     */
    getOpacity() {
        return this.currentOpacity;
    }
    
    /**
     * Check if currently transitioning
     * @returns {boolean}
     */
    isInTransition() {
        return this.isTransitioning;
    }
    
    /**
     * Setup debug keyboard controls (N = fade out, M = fade in)
     */
    setupDebugControls() {
        console.log('[OpacityController] Debug controls: N = fade out (night), M = fade in (day)');
        
        this.onKeyDown = (event) => {
            if (event.code === 'KeyN' && !this.isTransitioning) {
                console.log('[OpacityController] Debug: Fading out (revealing night)');
                this.fadeOut();
            } else if (event.code === 'KeyM' && !this.isTransitioning) {
                console.log('[OpacityController] Debug: Fading in (showing day)');
                this.fadeIn();
            }
        };
        
        window.addEventListener('keydown', this.onKeyDown);
    }
    
    onDestroy() {
        if (this.onKeyDown) {
            window.removeEventListener('keydown', this.onKeyDown);
        }
    }
}